/**
 * poopartnerConstants — client mirror of the four locked backend
 * constants exposed by `GET /api/public/poopartner/constants`.
 *
 * Hardcoded here for zero-runtime-dependency wizard rendering. Drift
 * against the backend is caught by:
 *   1. `poopartnerConstants.test.js` — byte-parity vs the expected
 *      shape locked in the test file (client-side lockdown).
 *   2. Backend `tests/poopartner/test_locked_constants.py` — byte-
 *      parity vs the Python constants.
 * Together the pair pins the client + server to the same values.
 *
 * When a future dispatch changes the backend constants, this file
 * AND `poopartnerConstants.test.js` must both be updated in the same
 * commit, and the backend drift-guard must also flip. The Phase 1a
 * dispatch enforces this discipline via a paired-update protocol.
 */

// 4 revenue bands, locked order.
export const POOPARTNER_REVENUE_BANDS = [
  { id: "band_1", label: "Less than $100K" },
  { id: "band_2", label: "$100K\u2013$500K" },
  { id: "band_3", label: "$500K\u2013$1M" },
  { id: "band_4", label: "$1M+" },
];

// 6 online-presence fields, locked order, all individually optional.
export const POOPARTNER_ONLINE_PRESENCE_FIELDS = [
  { id: "business_website", label: "Business website", type: "url" },
  { id: "facebook", label: "Facebook page", type: "url" },
  { id: "instagram", label: "Instagram", type: "url" },
  { id: "tiktok", label: "TikTok", type: "url" },
  { id: "youtube", label: "YouTube", type: "url" },
  {
    id: "other",
    label: "Other",
    type: "url",
    help_text: "Google Business, Yelp, Nextdoor, etc.",
  },
];

// Verbatim locked legal-acceptance text. Displayed above the Step 5
// checkbox AND snapshot on the acceptance row server-side.
export const POOPARTNER_INTAKE_LEGAL_TEXT =
  "I have read and agree to the Terms of Service, " +
  "the PooPartner Agreement, and the Privacy Policy.";

// Bumps to "v1" when counsel-reviewed docs land. Any change here
// must be paired with a backend constants bump and both drift-guards
// updated.
export const POOPARTNER_INTAKE_LEGAL_VERSION_ID = "placeholder-v0";

// 3 placeholder legal routes (Phase 1c). Real copy from counsel
// replaces the placeholder pages at these same paths.
export const POOPARTNER_LEGAL_ROUTES = {
  terms: "/legal/terms",
  partnerAgreement: "/legal/partner-agreement",
  privacy: "/legal/privacy",
};

// 5 business-only entity types with human labels for the wizard
// Step-1 dropdown. IDs are byte-identical to the backend EntityType
// enum values.
export const POOPARTNER_ENTITY_TYPES = [
  { id: "single_member_llc", label: "Single-Member LLC" },
  { id: "multi_member_llc", label: "Multi-Member LLC" },
  { id: "s_corp", label: "S-Corp" },
  { id: "c_corp", label: "C-Corp" },
  { id: "partnership", label: "Partnership" },
];

// W-9 upload constraints — mirror of backend
// POOPARTNER_W9_ALLOWED_MIMES / POOPARTNER_W9_MAX_BYTES.
export const POOPARTNER_W9_ALLOWED_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];
export const POOPARTNER_W9_MAX_BYTES = 10 * 1024 * 1024;

export const POOPARTNER_W9_IRS_URL =
  "https://www.irs.gov/pub/irs-pdf/fw9.pdf";
